import {testIdProps} from '@github-ui/test-id-props'
import {forwardRef, useEffect, useRef} from 'react'

import {agentMentionPattern, useAvailableAgents} from '../utils/agents-helpers'
import {referenceID} from '../utils/copilot-chat-helpers'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {ReferenceMention} from '../utils/reference-mention'
import styles from './ChatInput.module.css'

interface ChatInputPreviewProps {
  text: string
}

/**
 * This is displayed over the <textarea> for the ChatInput and is used to display rich text e.g. to highlight mentions and references.
 */
export const ChatInputPreview = forwardRef<HTMLDivElement, ChatInputPreviewProps>(function ChatInputPreview(
  {text},
  ref,
) {
  const tokens = text.split(/(\s+)/)

  // delay fetching agents until there is a potential mention
  const hasPossibleAgentMention = agentMentionPattern.test(tokens[0] ?? '')
  const {availableAgents} = useAvailableAgents(hasPossibleAgentMention)

  const {currentReferences} = useChatState()
  const manager = useChatManager()

  const mentionedReferences = new Set<string>()
  const highlightedTokens = tokens.map((token, i) => {
    const agentSlug = i === 0 && agentMentionPattern.exec(token)?.groups?.slug
    if (agentSlug && availableAgents?.some(agent => agent.slug === agentSlug))
      return (
        <span className={styles.token} key={i}>
          {token}
        </span>
      )

    const referenceMention = ReferenceMention.parse(token)
    const mentionedReference =
      referenceMention && currentReferences.find(r => ReferenceMention.refersTo(referenceMention, r))

    if (mentionedReference) {
      mentionedReferences.add(referenceID(mentionedReference))
      return (
        <span className={styles.token} key={i} {...testIdProps('input-preview-ref')}>
          {token}
        </span>
      )
    }

    return token
  })

  const previouslyMentionedReferences = useRef(mentionedReferences)
  useEffect(function removeReferencesForDeletedMentions() {
    for (const id of previouslyMentionedReferences.current)
      if (!mentionedReferences.has(id)) {
        const toRemove = currentReferences.find(r => referenceID(r) === id)
        if (toRemove) manager.removeReference(toRemove)
      }
    previouslyMentionedReferences.current = mentionedReferences
  })

  return (
    <div
      id="copilot-chat-textarea-preview"
      {...testIdProps('copilot-chat-input-textarea-preview')}
      aria-hidden
      className={styles.inputPreview}
      ref={ref}
      role="presentation"
    >
      {highlightedTokens}
    </div>
  )
})

try{ ChatInputPreview.displayName ||= 'ChatInputPreview' } catch {}